// @greenlight-spec: 59-platform-agent.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  BookCheck,
  CalendarClock,
  FlaskConical,
  GitPullRequest,
  Hand,
  LifeBuoy,
  MessageSquareText,
  MessagesSquare,
  Telescope,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';

import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { Tag } from '@/components/greenlight/Tag';
import { relativeTime, timeUntil } from '@/components/greenlight/relativeTime';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import {
  ACTION_KINDS,
  AGENTS,
  SESSIONS,
  type ActionKind,
  type ActionRequest,
  type SessionDef,
  type SessionStatus,
  type TriggerKind,
} from './fixtures';

export const agentById = (id: string) => AGENTS.find((a) => a.id === id);
export const sessionById = (id: string) => SESSIONS.find((s) => s.id === id);
export const kindById = (id: ActionKind) => ACTION_KINDS.find((k) => k.id === id)!;

const byNewest = (a: SessionDef, b: SessionDef) => b.startedAt.localeCompare(a.startedAt);
export const sessionsFor = (agentId: string) =>
  SESSIONS.filter((s) => s.agentId === agentId).sort(byNewest);
export const latestSession = (agentId: string) => sessionsFor(agentId)[0];
export const runningSessions = () => SESSIONS.filter((s) => s.status === 'running').sort(byNewest);

/** A session as shown for the target a person picked when they started it (`?target=`). */
export function resolveSession(session: SessionDef, pickedTarget: string | null) {
  const target = pickedTarget ?? session.target ?? '';
  const by = pickedTarget ? 'you' : session.trigger.by;
  return {
    title:
      pickedTarget && session.titleTemplate
        ? session.titleTemplate.replace('{target}', pickedTarget)
        : session.title,
    target,
    trigger: pickedTarget ? { kind: 'human' as const, by } : session.trigger,
    transcript: session.transcriptFor ? session.transcriptFor(target, by) : session.transcript,
  };
}

export const isRoadmap = (agentId: string) => agentById(agentId)?.status === 'roadmap';

/** Marks an agent that is not in this release; its fixtures stay to show the design. */
export function RoadmapTag({ className }: { className?: string }) {
  return <Tag className={cn('bg-muted px-2 py-0 text-[11px] font-medium', className)}>Roadmap</Tag>;
}

const AGENT_ICON: Record<string, LucideIcon> = {
  'integration-tester': FlaskConical,
  'knowledge-explorer': Telescope,
  'knowledge-curator': BookCheck,
  'app-patcher': Wrench,
  'app-code-reviewer': GitPullRequest,
  'update-failure-fixer': LifeBuoy,
  'feedback-triage': MessagesSquare,
  'health-watcher': Activity,
  assistant: MessageSquareText,
};

/** An agent's icon tile: indigo marks agent-driven work (docs/19). */
export function AgentTile({
  agentId,
  size = 'md',
  muted,
}: {
  agentId: string;
  size?: 'sm' | 'md';
  muted?: boolean;
}) {
  const Icon = AGENT_ICON[agentId] ?? MessageSquareText;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-gl-icon',
        muted ? 'bg-muted text-slate' : 'bg-glow-indigo text-indigo-ink',
        size === 'sm' ? 'size-7 [&_svg]:size-3.5' : 'size-10 [&_svg]:size-[18px]',
      )}
      aria-hidden
    >
      <Icon />
    </span>
  );
}

const STATUS_META: Record<SessionStatus, { variant: StatusBadgeVariant; label: string }> = {
  running: { variant: 'deploying', label: 'Running' },
  waiting: { variant: 'pending', label: 'Waiting on you' },
  done: { variant: 'active', label: 'Done' },
  failed: { variant: 'failed', label: 'Failed' },
  queued: { variant: 'inactive', label: 'Queued' },
};

export function SessionStatusBadge({ status }: { status: SessionStatus }) {
  const m = STATUS_META[status];
  return <StatusBadge variant={m.variant}>{m.label}</StatusBadge>;
}

const TRIGGER_ICON: Record<TriggerKind, React.ComponentType<{ className?: string }>> = {
  human: Hand,
  schedule: CalendarClock,
  event: Zap,
};

const TRIGGER_WORD: Record<TriggerKind, string> = {
  human: 'On request',
  schedule: 'Schedule',
  event: 'Event',
};

/** A trigger as a compact pill: the icon plus a short word, the detail on hover. */
export function TriggerPill({ kind, detail }: { kind: TriggerKind; detail: string }) {
  const Icon = TRIGGER_ICON[kind];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[11.5px] font-medium"
      title={detail}
    >
      <Icon className="size-3 shrink-0" aria-hidden />
      {TRIGGER_WORD[kind]}
      <span className="sr-only">: {detail}</span>
    </span>
  );
}

export function TriggerChip({ kind, by }: { kind: TriggerKind; by: string }) {
  const Icon = TRIGGER_ICON[kind];
  return (
    <span className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
      <Icon className="size-3.5 shrink-0" aria-hidden />
      <span className="truncate">{by}</span>
    </span>
  );
}

export function Ago({ iso }: { iso: string }) {
  if (!iso) return <span className="text-muted-foreground">—</span>;
  return (
    <time dateTime={iso} title={new Date(iso).toLocaleString()}>
      {relativeTime(iso)}
    </time>
  );
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
export const usd = (n: number) => USD.format(n);
export const tokens = (n: number) => `${Math.round(n / 1000)}k tokens`;

/**
 * One pending action a session is blocked on. The body is per kind: what a
 * person wants to see before saying yes. Approve runs the stored parameters on
 * the control plane; the agent is never re-asked.
 */
export function ActionRequestCard({
  request,
  compact,
  onDecide,
}: {
  request: ActionRequest;
  compact?: boolean;
  onDecide?: (decision: 'approved' | 'denied') => void;
}) {
  const kind = kindById(request.kind);
  const agent = agentById(request.agentId);
  const pending = request.status === 'pending';
  return (
    <div
      className={cn(
        'rounded-gl-lg border bg-card p-4',
        pending ? 'border-[hsl(var(--status-amber)/0.5)]' : 'border-border',
      )}
      data-testid={`action-request-${request.id}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="flex items-center gap-2">
          <span className="text-[13.5px] font-semibold">{kind.label}</span>
          {agent?.status === 'roadmap' && <RoadmapTag />}
          {!pending && (
            <StatusBadge variant={request.status === 'approved' ? 'granted' : 'revoked'}>
              {request.status === 'approved'
                ? 'Approved'
                : request.status === 'denied'
                  ? 'Denied'
                  : 'Expired'}
            </StatusBadge>
          )}
        </div>
        <span className="text-[12px] text-muted-foreground">
          {agent?.name} · <Ago iso={request.requestedAt} />
          {pending && <> · expires {timeUntil(request.expiresAt)}</>}
        </span>
      </div>
      <div className="mt-1.5 text-[13.5px]">
        <span className="text-muted-foreground">{request.target}</span> ·{' '}
        <span className="font-medium">{request.summary}</span>
      </div>
      {!compact && (
        <ul className="mt-2 grid gap-0.5 text-[12.5px] text-muted-foreground sm:grid-cols-2">
          {request.evidence.map((e) => (
            <li key={e} className="flex items-start gap-1.5">
              <span
                className="mt-[7px] size-1 shrink-0 rounded-full bg-[hsl(var(--status-green))]"
                aria-hidden
              />
              {e}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {pending && onDecide && (
          <>
            <Button size="sm" onClick={() => onDecide('approved')}>
              {request.kind === 'merge_pr' ? 'Approve and merge' : 'Approve'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDecide('denied')}>
              Deny
            </Button>
          </>
        )}
        <span className="ml-auto flex items-center gap-3 text-[12.5px]">
          <Link
            className="text-link hover:underline"
            to={`/agents/${request.agentId}/sessions/${request.sessionId}`}
          >
            Open session
          </Link>
          {request.kind === 'merge_pr' && (
            <a className="text-link hover:underline" href="#" onClick={(e) => e.preventDefault()}>
              View PR
            </a>
          )}
        </span>
      </div>
    </div>
  );
}
